import * as React from 'react'

import { Indicator } from 'is24-corecss'
import { Link as RouterLink } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import { FLOWS } from '../../../app/AppRouter'
import { FlowName } from '../../../@types/flow-types'
import styles from './FlowChooserPage.module.css'

const flowGroups = [
  { title: 'Property owner', prefix: 'property_owner_' },
  { title: 'Tenant', prefix: 'tenant_' },
  { title: 'Agent', prefix: 'agent_' },
] as const

const getFlowTitle = (flowName: string, prefix: string) =>
  flowName
    .replace(prefix, '')
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

/**
 * Only used in local setup to show a list of all available flows.
 */
const FlowChooserPage: React.FC = () => {
  const publishedListingApartmentRentUuid = React.useMemo(() => uuid(), [])
  const agentPublishedListingApartmentRentUuid = React.useMemo(() => uuid(), [])
  const publishedListingHouseSellUuid = React.useMemo(() => uuid(), [])

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <p className={styles.eyebrow}>Local development</p>
        <h1>Choose a flow</h1>
        <p className={styles.description}>Open a listing flow with the browser or mobile-view variants you need.</p>
      </header>

      <div className={styles.groups}>
        {flowGroups.map(({ title, prefix }) => {
          const flows = FLOWS.filter((flow) => flow.flowInformation.name.startsWith(prefix))

          return (
            <section className={styles.group} key={title} aria-labelledby={`${prefix}flows`}>
              <div className={styles.groupHeader}>
                <h2 id={`${prefix}flows`}>{title}</h2>
                <Indicator.Root appearance='label' color='brand' variant='flat' size='small'>
                  {flows.length} flows
                </Indicator.Root>
              </div>

              <div className={styles.flowList}>
                {flows.map((flow) => (
                  <article className={styles.flow} key={flow.flowInformation.name}>
                    <h3>{getFlowTitle(flow.flowInformation.name, prefix)}</h3>
                    <div className={styles.actions}>
                      <RouterLink to={flow.flowInformation.path} className={styles.primaryAction}>
                        Open flow
                      </RouterLink>
                      <RouterLink to={`${flow.flowInformation.path}?isMobileView=true`} className={styles.action}>
                        Mobile
                      </RouterLink>
                      {flow.flowInformation.name === FlowName.PROPERTY_OWNER_APARTMENT_RENT && (
                        <RouterLink
                          to={`${flow.flowInformation.path}/published-listing-apartment-rent-${publishedListingApartmentRentUuid}`}
                          className={styles.action}
                        >
                          Published
                        </RouterLink>
                      )}
                      {flow.flowInformation.name === FlowName.AGENT_APARTMENT_RENT && (
                        <RouterLink
                          to={`${flow.flowInformation.path}/agent-published-listing-apartment-rent-${agentPublishedListingApartmentRentUuid}`}
                          className={styles.action}
                        >
                          Published
                        </RouterLink>
                      )}
                      {flow.flowInformation.name === FlowName.PROPERTY_OWNER_HOUSE_SELL && (
                        <RouterLink
                          to={`${flow.flowInformation.path}/published-listing-house-sell-${publishedListingHouseSellUuid}`}
                          className={styles.action}
                        >
                          Published
                        </RouterLink>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )
        })}
      </div>
    </main>
  )
}

export default FlowChooserPage
